import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { CreditCard, ExternalLink, ChevronRight } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';
import { useEnforcement } from '@/hooks/useEnforcement';
import { useSelection } from '@/contexts/SelectionContext';
import { openCustomerPortal } from '@/lib/checkout';
import { numberLocale } from '@/lib/language';
import { SUPPORT_EMAIL, billingAccountKind, exceedsLargestBand, planDisplayName, planTierLabel } from '@/lib/plans';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import { PlanBandManager } from '@/components/settings/PlanBandManager';
import { SubscriptionRetentionCard } from '@/components/settings/SubscriptionRetentionCard';

/**
 * Settings → Abonnement (aus Settings.tsx herausgeloest, UX-Audit A6G-02/K48).
 *   - Portal-Knopf nur mit Stripe-Kunde; Sondertarife ohne Kunde sehen den
 *     Support-Hinweis. Vorher antwortete customer-portal 500 und nichts passierte.
 *   - Portal-Fehler immer als Toast.
 *   - Arbeitet der Nutzer in einer fremden Merchant Center, steht deren
 *     Kontingent beschriftet und getrennt vom eigenen Tarif.
 */
const SubscriptionTab: React.FC = () => {
  const { t, i18n } = useTranslation('settings');
  const { subscription } = useSubscription();
  const { count, limit, usagePct, totalProducts, recommendedPlan, isMemberOfForeignMc, billedOwnerEmail, planKnown } = useEnforcement();
  const { merchantCenter } = useSelection();
  const [portalLoading, setPortalLoading] = useState(false);

  const nf = (n: number) => n.toLocaleString(numberLocale(i18n.language));
  const kind = billingAccountKind(subscription);
  const canOpenPortal = !!subscription?.stripe_customer_id;
  const foreign = isMemberOfForeignMc && !!billedOwnerEmail;
  // A6-NEU-2: dieselbe Regel wie in der Seitenleiste (UsageWidget). Ist das
  // Kontingent des Eigentuemers nicht lesbar, traegt useEnforcement Vorgabewerte
  // (0 / 200); die duerfen nicht als seine Zahlen dastehen.
  const quotaUnknown = foreign && !planKnown;
  // A7-NEU-1: deckt selbst das groesste Band den Bestand nicht, gibt es nichts
  // zu empfehlen (gleicher Satz wie Banner und Kapazitaets-Dialog, A7O-06).
  const beyondBands = !!recommendedPlan && exceedsLargestBand(totalProducts);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      await openCustomerPortal();
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'billing', action: 'manage-subscription' } });
      toast.error(t('subscription.portalError', { email: SUPPORT_EMAIL }));
      setPortalLoading(false);
    }
  };

  // Band-Tiers wuerden kapitalisiert "Cap1000" heissen; der Label kommt aus
  // PLANS_BY_TIER (Baender lokalisiert als "1.000 Produkte", Legacy-Eigennamen).
  const planLabel = subscription?.plan_tier
    ? planTierLabel(subscription.plan_tier, i18n?.language, t)
    : 'Free';
  const isCancelling = subscription?.cancel_at_period_end === true && subscription?.status !== 'canceled';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          {t('subscription.title')}
        </CardTitle>
        <CardDescription>{t('subscription.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {/* "Tarif: {{plan}}" statt "{{plan}} Tarif": Baender heissen
                "1.000 Produkte", und "1.000 Produkte Tarif" waere kein Deutsch. */}
            <p className="font-medium">{t('subscription.planTitle', { plan: planLabel })}</p>
            <p className="text-sm text-muted-foreground">
              {t('subscription.capacity', { limit: nf(subscription?.product_limit ?? 200) })}
              {subscription?.cancel_at_period_end && ` • ${t('subscription.cancelsAtEnd')}`}
            </p>
          </div>
          {canOpenPortal ? (
            <Button
              variant="outline"
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="gap-2"
            >
              {portalLoading ? <Spinner size="sm" /> : <ExternalLink className="h-4 w-4" />}
              {t('subscription.manage')}
            </Button>
          ) : kind === 'special' ? (
            <p className="max-w-xs text-sm text-muted-foreground">
              {t('subscription.managedBySupport', { email: SUPPORT_EMAIL })}
            </p>
          ) : null}
        </div>

        <div className="space-y-2 rounded-md">
          {foreign && (
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('subscription.foreignQuotaTitle', { email: billedOwnerEmail })}</p>
              <p className="text-xs text-muted-foreground">
                {merchantCenter?.name
                  ? t('subscription.foreignQuotaNote', { mc: merchantCenter.name, email: billedOwnerEmail })
                  : t('subscription.foreignQuotaNoteNoMc', { email: billedOwnerEmail })}
              </p>
            </div>
          )}
          {quotaUnknown ? (
            <p className="text-sm text-muted-foreground" data-testid="subscription-quota-pending">
              {t('quotaPending', { ns: 'enforcement' })}
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t('subscription.optimizationsUsed')}</span>
                <span className="font-medium">{nf(count)} / {nf(limit)}</span>
              </div>
              <Progress value={Math.min(usagePct, 100)} className="h-2" />
            </>
          )}
          {/* Die Warnung spricht vom EIGENEN Tarif; beim fremden Kontingent
              waere sie falsch adressiert (K48). */}
          {!foreign && totalProducts > limit && (
            <p className="text-xs text-warning-ink">
              {t('subscription.overLimitWarning', { totalProducts: nf(totalProducts), limit: nf(limit) })}
              {beyondBands ? (
                <>
                  {' '}{t('modal.noBandCovers', { ns: 'enforcement', total: nf(totalProducts) })}{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold underline">
                    {t('modal.contactCta', { ns: 'enforcement' })}
                  </a>
                </>
              ) : recommendedPlan && (
                // Kein Leerzeichen vor planDot: DE ist das ein blanker Punkt,
                // EN traegt sein fuehrendes Leerzeichen im Wert (" band.").
                <> {t('subscription.weRecommend')} <span className="font-semibold">{planDisplayName(recommendedPlan, i18n?.language, t)}</span>{t('subscription.planDot')}</>
              )}
            </p>
          )}
        </div>

        {/* Add-ons (MC/Zielsprachen) und Bandwechsel fuer Bestandskunden. */}
        <PlanBandManager subscription={subscription} />

        {isCancelling ? (
          <SubscriptionRetentionCard
            currentPeriodEnd={subscription?.current_period_end ?? null}
            onManage={handleManageSubscription}
            loading={portalLoading}
          />
        ) : (
          <Link to="/pricing">
            <Button variant="outline" className="gap-2">
              {t('subscription.viewAllPlans')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionTab;
